import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined, AccessTimeOutlined, PlayArrowOutlined, PauseOutlined, ReplayOutlined, SkipNextOutlined, StopOutlined, EditNoteOutlined } from '../../icons';
import './StudyTimer.css';

const FOCUS_SECONDS = 25 * 60;
const REST_SECONDS = 5 * 60;
const LIVRE_PRESETS = [15, 30, 45, 60];
const RING_RADIUS = 112;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

// Modal do cronômetro Pomodoro, aberto pelo botão ao lado do título "Estudo do
// dia" (TodayStudyPanel.js). Baseado no mockup "15a - Cronômetro de Estudos",
// mas sem o texto de matéria/sessões do mockup — só o pomodoro em si.
const StudyTimerModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('pomodoro'); // 'pomodoro' | 'livre'
  const [phase, setPhase] = useState('foco'); // 'foco' | 'descanso' (só pomodoro)
  const [livreMinutes, setLivreMinutes] = useState(30);
  const [secondsLeft, setSecondsLeft] = useState(FOCUS_SECONDS);
  const [running, setRunning] = useState(false);
  const [studiedSeconds, setStudiedSeconds] = useState(0); // tempo de foco/livre acumulado na sessão
  const [finished, setFinished] = useState(false);

  const totalSeconds = mode === 'pomodoro'
    ? (phase === 'foco' ? FOCUS_SECONDS : REST_SECONDS)
    : livreMinutes * 60;

  useEffect(() => {
    if (!running) return undefined;
    const intervalId = setInterval(() => {
      if (mode === 'pomodoro' && phase === 'foco') {
        setStudiedSeconds((prev) => prev + 1);
      } else if (mode === 'livre') {
        setStudiedSeconds((prev) => prev + 1);
      }

      setSecondsLeft((prev) => {
        if (prev > 1) return prev - 1;

        if (mode === 'pomodoro') {
          setPhase((prevPhase) => (prevPhase === 'foco' ? 'descanso' : 'foco'));
        } else {
          setRunning(false);
          setFinished(true);
        }
        return 0;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [running, mode, phase]);

  // Ao trocar de fase (fim de ciclo ou "pular"), recarrega o tempo cheio da nova fase.
  useEffect(() => {
    if (mode === 'pomodoro') {
      setSecondsLeft(phase === 'foco' ? FOCUS_SECONDS : REST_SECONDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setRunning(false);
    setFinished(false);
    setStudiedSeconds(0);
    setMode(nextMode);
    if (nextMode === 'pomodoro') {
      setPhase('foco');
      setSecondsLeft(FOCUS_SECONDS);
    } else {
      setSecondsLeft(livreMinutes * 60);
    }
  };

  const handlePlayPause = () => {
    if (secondsLeft === 0) {
      setSecondsLeft(mode === 'pomodoro' ? (phase === 'foco' ? FOCUS_SECONDS : REST_SECONDS) : livreMinutes * 60);
    }
    setRunning((prev) => !prev);
  };

  const handleReset = () => {
    setRunning(false);
    setFinished(false);
    setStudiedSeconds(0);
    setSecondsLeft(mode === 'pomodoro' ? (phase === 'foco' ? FOCUS_SECONDS : REST_SECONDS) : livreMinutes * 60);
  };

  const handleSkip = () => {
    setPhase((prev) => (prev === 'foco' ? 'descanso' : 'foco'));
  };

  const handlePresetSelect = (minutes) => {
    setRunning(false);
    setLivreMinutes(minutes);
    setSecondsLeft(minutes * 60);
  };

  const handleStop = () => {
    setRunning(false);
    setFinished(true);
  };

  const handleRegisterStudy = () => {
    const durationMinutes = Math.max(1, Math.round(studiedSeconds / 60));
    onClose();
    navigate('/diario-de-estudos', { state: { durationMinutes } });
  };

  const fraction = totalSeconds > 0 ? secondsLeft / totalSeconds : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - fraction);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="study-timer-card" onClick={(e) => e.stopPropagation()}>
        <div className="study-timer-header">
          <span className="study-timer-header-icon">
            <AccessTimeOutlined style={{ fontSize: 16 }} />
          </span>
          <strong>Cronômetro de estudos</strong>
          <span className="study-timer-header-sub">— Pomodoro ou tempo livre</span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
            <CloseOutlined style={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="study-timer-body">
          <div className="study-timer-tabs">
            <button
              type="button"
              className={`study-timer-tab ${mode === 'pomodoro' ? 'study-timer-tab--active' : ''}`}
              onClick={() => handleModeChange('pomodoro')}
            >
              Pomodoro
            </button>
            <button
              type="button"
              className={`study-timer-tab ${mode === 'livre' ? 'study-timer-tab--active' : ''}`}
              onClick={() => handleModeChange('livre')}
            >
              Tempo livre
            </button>
          </div>

          {mode === 'pomodoro' && (
            <div className="study-timer-badges">
              <span className={`study-timer-badge ${phase === 'foco' ? 'study-timer-badge--active' : ''}`}>
                Foco · 25 min
              </span>
              <span className={`study-timer-badge ${phase === 'descanso' ? 'study-timer-badge--active' : ''}`}>
                Descanso · 5 min
              </span>
            </div>
          )}

          {finished ? (
            <div className="study-timer-finished">
              <span className="study-timer-finished-icon">
                <EditNoteOutlined style={{ fontSize: 32 }} />
              </span>
              <span className="study-timer-finished-title">Sessão encerrada</span>
              <span className="study-timer-finished-time">{formatTime(studiedSeconds)}</span>
              <span className="study-timer-finished-label">de estudo nessa sessão</span>
              <button type="button" className="study-timer-register-btn" onClick={handleRegisterStudy}>
                <EditNoteOutlined style={{ fontSize: 18 }} />
                Registrar estudo
              </button>
              <button type="button" className="study-timer-finished-dismiss" onClick={handleReset}>
                Iniciar nova sessão
              </button>
            </div>
          ) : (
            <>
              <div className="study-timer-ring">
                <svg viewBox="0 0 260 260">
                  <circle cx="130" cy="130" r={RING_RADIUS} className="study-timer-ring-track" />
                  <circle
                    cx="130"
                    cy="130"
                    r={RING_RADIUS}
                    className="study-timer-ring-progress"
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={dashOffset}
                  />
                </svg>
                <div className="study-timer-time">
                  <span className="study-timer-time-value">{formatTime(secondsLeft)}</span>
                  <span className="study-timer-time-label">
                    {mode === 'pomodoro' ? (phase === 'foco' ? 'restantes de foco' : 'restantes de descanso') : 'restantes'}
                  </span>
                </div>
              </div>

              <div className="study-timer-controls">
                <button type="button" className="study-timer-control-btn" onClick={handleReset} aria-label="Reiniciar ciclo">
                  <ReplayOutlined style={{ fontSize: 18 }} />
                </button>
                <button
                  type="button"
                  className="study-timer-control-btn study-timer-control-btn--primary"
                  onClick={handlePlayPause}
                  aria-label={running ? 'Pausar' : 'Iniciar'}
                >
                  {running ? <PauseOutlined style={{ fontSize: 26 }} /> : <PlayArrowOutlined style={{ fontSize: 26 }} />}
                </button>
                {mode === 'pomodoro' && (
                  <button type="button" className="study-timer-control-btn" onClick={handleSkip} aria-label="Pular para a próxima etapa">
                    <SkipNextOutlined style={{ fontSize: 18 }} />
                  </button>
                )}
              </div>

              {studiedSeconds > 0 && (
                <button type="button" className="study-timer-stop-btn" onClick={handleStop}>
                  <StopOutlined style={{ fontSize: 16 }} />
                  Encerrar sessão
                </button>
              )}
            </>
          )}
        </div>

        {mode === 'livre' && !finished && (
          <div className="study-timer-footer">
            <span className="study-timer-footer-label">TEMPO LIVRE — DEFINIR MANUALMENTE</span>
            <div className="study-timer-presets">
              {LIVRE_PRESETS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  className={`study-timer-preset ${livreMinutes === minutes ? 'study-timer-preset--active' : ''}`}
                  onClick={() => handlePresetSelect(minutes)}
                >
                  {minutes} min
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyTimerModal;
